import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, MessageSquarePlus, Paperclip } from "lucide-react";
import config from "../../config/config";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { clearStoredAuth } from "../../utils/authSession";
import {
  buildKvpClientContext,
  buildKvpContextLines,
  KVP_DATA_SCOPE_OPTIONS,
  KVP_FLOW_COMPLETED_OPTIONS,
  KVP_GOAL_OPTIONS,
  KVP_IMPACT_OPTIONS,
  KVP_IMPROVEMENT_FOCUS_OPTIONS,
  KVP_ISSUE_OPTIONS,
} from "../../utils/kvpContext";
import { buildAuthHeaders, buildAuthRequestConfig } from "../../utils/authSession";

function authHeaders() {
  return buildAuthHeaders();
}

function createInitialForm() {
  return {
    issue_type: "technical_error",
    impact_level: "difficult",
    goal_reached: "partly",
    description: "",
    wants_contact: true,
    include_field_value: false,
    error_visible: "",
    flow_completed: "partly",
    data_scope: "both",
    improvement_focus: "clarity",
  };
}

function formatDateTime(value) {
  if (!value) return "n/a";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function humanizeStatus(value) {
  const map = {
    new: "Neu",
    triage: "Sichtung",
    need_info: "Rueckfrage",
    planned: "Geplant",
    in_progress: "In Arbeit",
    done: "Erledigt",
    declined: "Abgelehnt",
    reopened: "Wieder geoeffnet",
  };
  return map[value] || value || "n/a";
}

export default function KvpReportTrigger({
  context,
  currentFieldValue = "",
  className = "",
  label = "",
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(createInitialForm());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [createdReport, setCreatedReport] = useState(null);
  const [myReports, setMyReports] = useState([]);
  const [myReportsLoading, setMyReportsLoading] = useState(false);
  const [myReportsError, setMyReportsError] = useState("");
  const [selectedReportId, setSelectedReportId] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedReportLoading, setSelectedReportLoading] = useState(false);
  const [selectedReportError, setSelectedReportError] = useState("");

  const resolvedContext = useMemo(() => buildKvpClientContext(context), [context]);
  const contextLines = useMemo(() => buildKvpContextLines(resolvedContext), [resolvedContext]);

  const handleAuthError = (err) => {
    if (Number(err?.response?.status) !== 401) return false;
    clearStoredAuth();
    window.location.assign("/login");
    return true;
  };

  const patchForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const resetState = () => {
    setForm(createInitialForm());
    setFile(null);
    setError("");
    setSuccess("");
    setCreatedReport(null);
    setMyReportsError("");
    setSelectedReportError("");
  };

  const loadMyReports = async (preferredReportId = "") => {
    setMyReportsLoading(true);
    setMyReportsError("");
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/kvp/my-reports`, {
        ...buildAuthRequestConfig({ headers: authHeaders() }),
      });
      const rows = Array.isArray(response.data?.rows) ? response.data.rows : [];
      setMyReports(rows);
      const nextId = preferredReportId || selectedReportId || rows[0]?.id || "";
      setSelectedReportId(nextId ? String(nextId) : "");
      return rows;
    } catch (err) {
      if (handleAuthError(err)) return [];
      setMyReports([]);
      setMyReportsError(err?.response?.data?.message || "Deine Meldungen konnten nicht geladen werden.");
      return [];
    } finally {
      setMyReportsLoading(false);
    }
  };

  const loadReportDetail = async (reportId) => {
    if (!reportId) {
      setSelectedReport(null);
      return;
    }
    setSelectedReportLoading(true);
    setSelectedReportError("");
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/kvp/my-reports/${reportId}`, {
        ...buildAuthRequestConfig({ headers: authHeaders() }),
      });
      setSelectedReport(response.data?.report || null);
    } catch (err) {
      if (handleAuthError(err)) return;
      setSelectedReport(null);
      setSelectedReportError(err?.response?.data?.message || "Die Rueckmeldung zu dieser Meldung konnte nicht geladen werden.");
    } finally {
      setSelectedReportLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.description.trim()) {
      setError("Bitte beschreibe kurz, was das Problem ist oder was verbessert werden sollte.");
      return;
    }

    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        issue_type: form.issue_type,
        impact_level: form.impact_level,
        goal_reached: form.goal_reached,
        description: form.description.trim(),
        wants_contact: form.wants_contact,
        include_field_value: form.include_field_value,
        current_field_value: form.include_field_value ? currentFieldValue : null,
        error_visible:
          form.issue_type === "technical_error" && form.error_visible !== ""
            ? form.error_visible === "yes"
            : undefined,
        flow_completed:
          form.issue_type === "technical_error" ? form.flow_completed : undefined,
        data_scope:
          form.issue_type === "data_incorrect" ? form.data_scope : undefined,
        improvement_focus:
          ["improvement_suggestion", "change_request"].includes(form.issue_type)
            ? form.improvement_focus
            : undefined,
        ...resolvedContext,
      };

      const reportResponse = await axios.post(
        `${config.API_BASE_URL}/api/kvp/reports`,
        payload,
        buildAuthRequestConfig({ headers: authHeaders() })
      );
      const report = reportResponse.data?.report || null;
      if (report?.id && file) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("label", "Screenshot");
        await axios.post(
          `${config.API_BASE_URL}/api/kvp/reports/${report.id}/attachments`,
          formData,
          buildAuthRequestConfig({
            headers: {
              ...authHeaders(),
              "Content-Type": "multipart/form-data",
            },
          })
        );
      }
      setCreatedReport(report);
      setSuccess(
        report?.public_id
          ? `Meldung ${report.public_id} wurde gespeichert.`
          : "Deine Meldung wurde gespeichert."
      );
      setFile(null);
      await loadMyReports(report?.id);
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err?.response?.data?.message || "Die Meldung konnte nicht gespeichert werden.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    loadMyReports();
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open || !selectedReportId) return undefined;
    loadReportDetail(selectedReportId);
    return undefined;
  }, [open, selectedReportId]);

  return (
    <>
      <button
        type="button"
        className={`kvp-trigger-button${className ? ` ${className}` : ""}`}
        onClick={() => setOpen(true)}
        aria-label="Problem oder Verbesserung melden"
        title="Problem oder Verbesserung melden"
      >
        <MessageSquarePlus size={16} />
        {label ? <span>{label}</span> : null}
      </button>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) resetState();
        }}
      >
        {open ? (
          <DialogContent className="kvp-dialog">
            <DialogHeader className="kvp-dialog__header">
              <DialogTitle className="kvp-dialog__title">Problem oder Verbesserung melden</DialogTitle>
              <DialogDescription className="kvp-dialog__description">
                Der Kontext wird automatisch aus der aktuellen Stelle uebernommen. Bitte beschreibe nur kurz dein Anliegen.
              </DialogDescription>
            </DialogHeader>

            <div className="kvp-context-card">
              <span className="kvp-context-card__title">Kontext</span>
              <div className="kvp-context-grid">
                {contextLines.map((entry) => (
                  <div key={`${entry.label}-${entry.value}`} className="kvp-context-grid__item">
                    <span>{entry.label}</span>
                    <strong>{entry.value}</strong>
                  </div>
                ))}
              </div>
            </div>

            {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
            {success ? <div className="dashboard-inline-alert dashboard-inline-alert-success">{success}</div> : null}

            {!success ? (
              <form onSubmit={handleSubmit} className="kvp-form">
                <label className="kvp-form__field">
                  <span>Anliegenart</span>
                  <select
                    className="kvp-select"
                    value={form.issue_type}
                    onChange={(event) => patchForm("issue_type", event.target.value)}
                  >
                    {KVP_ISSUE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="kvp-form__row">
                  <label className="kvp-form__field">
                    <span>Auswirkung</span>
                    <select
                      className="kvp-select"
                      value={form.impact_level}
                      onChange={(event) => patchForm("impact_level", event.target.value)}
                    >
                      {KVP_IMPACT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="kvp-form__field">
                    <span>Ziel erreicht?</span>
                    <select
                      className="kvp-select"
                      value={form.goal_reached}
                      onChange={(event) => patchForm("goal_reached", event.target.value)}
                    >
                      {KVP_GOAL_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>

                {form.issue_type === "technical_error" ? (
                  <div className="kvp-form__row">
                    <label className="kvp-form__field">
                      <span>Ist eine Fehlermeldung erschienen?</span>
                      <select
                        className="kvp-select"
                        value={form.error_visible}
                        onChange={(event) => patchForm("error_visible", event.target.value)}
                      >
                        <option value="">Bitte waehlen</option>
                        <option value="yes">Ja</option>
                        <option value="no">Nein</option>
                      </select>
                    </label>
                    <label className="kvp-form__field">
                      <span>Konnte der Vorgang abgeschlossen werden?</span>
                      <select
                        className="kvp-select"
                        value={form.flow_completed}
                        onChange={(event) => patchForm("flow_completed", event.target.value)}
                      >
                        {KVP_FLOW_COMPLETED_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                ) : null}

                {form.issue_type === "data_incorrect" ? (
                  <label className="kvp-form__field">
                    <span>Betrifft es Anzeige / Speicherung / beides?</span>
                    <select
                      className="kvp-select"
                      value={form.data_scope}
                      onChange={(event) => patchForm("data_scope", event.target.value)}
                    >
                      {KVP_DATA_SCOPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                {["improvement_suggestion", "change_request"].includes(form.issue_type) ? (
                  <label className="kvp-form__field">
                    <span>Was sollte verbessert werden?</span>
                    <select
                      className="kvp-select"
                      value={form.improvement_focus}
                      onChange={(event) => patchForm("improvement_focus", event.target.value)}
                    >
                      {KVP_IMPROVEMENT_FOCUS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}

                <label className="kvp-form__field">
                  <span>Beschreibung</span>
                  <Textarea
                    rows={5}
                    className="applicant-field kvp-textarea"
                    value={form.description}
                    onChange={(event) => patchForm("description", event.target.value)}
                    placeholder="Was genau ist das Problem oder was sollte verbessert werden?"
                  />
                </label>

                <div className="kvp-form__field">
                  <span>Screenshot anhaengen</span>
                  <label className="kvp-upload-field">
                    <Paperclip size={15} />
                    <span>{file ? file.name : "PNG, JPG oder WEBP waehlen"}</span>
                    <Input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="kvp-upload-field__input"
                      onChange={(event) => setFile(event.target.files?.[0] || null)}
                    />
                  </label>
                </div>

                <label className="kvp-toggle-row">
                  <input
                    type="checkbox"
                    checked={form.wants_contact}
                    onChange={(event) => patchForm("wants_contact", event.target.checked)}
                  />
                  <span>Ich moechte bei Rueckfragen kontaktiert werden.</span>
                </label>

                <label className="kvp-toggle-row">
                  <input
                    type="checkbox"
                    checked={form.include_field_value}
                    onChange={(event) => patchForm("include_field_value", event.target.checked)}
                    disabled={!currentFieldValue}
                  />
                  <span>Aktuellen Feldwert mitsenden.</span>
                </label>

                {form.include_field_value && currentFieldValue ? (
                  <div className="kvp-inline-note">
                    Gesendet wird nur der aktuelle sichtbare Wert dieser Stelle: <strong>{currentFieldValue}</strong>
                  </div>
                ) : null}

                <div className="kvp-form__actions">
                  <Button
                    type="button"
                    variant="outline"
                    className="applicant-cta-button applicant-cta-button-secondary"
                    onClick={() => setOpen(false)}
                    disabled={busy}
                  >
                    Abbrechen
                  </Button>
                  <Button type="submit" className="applicant-cta-button" disabled={busy}>
                    {busy ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Speichert...</span>
                      </>
                    ) : (
                      <span>Meldung senden</span>
                    )}
                  </Button>
                </div>
              </form>
            ) : (
              <div className="kvp-success-card">
                <p>
                  {createdReport?.public_id
                    ? `Die Meldung ${createdReport.public_id} ist jetzt in der Admin-Inbox.`
                    : "Die Meldung ist jetzt in der Admin-Inbox."}
                </p>
                <div className="kvp-form__actions">
                  <Button className="applicant-cta-button" onClick={() => setOpen(false)}>
                    Schliessen
                  </Button>
                </div>
              </div>
            )}

            <div className="kvp-report-stream">
              <div className="kvp-report-stream__head">
                <div>
                  <span className="kvp-context-card__title">Eigene Meldungen</span>
                  <strong>Rueckmeldungen aus der Bearbeitung</strong>
                </div>
              </div>

              {myReportsError ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{myReportsError}</div> : null}

              {!myReportsLoading && !myReports.length ? (
                <div className="kvp-report-stream__empty">Noch keine KVP-Meldung vorhanden.</div>
              ) : null}

              {myReports.length ? (
                <div className="kvp-report-stream__layout">
                  <div className="kvp-report-list">
                    {myReports.map((report) => (
                      <button
                        key={report.id}
                        type="button"
                        className={`kvp-report-list__item${String(report.id) === String(selectedReportId) ? " is-active" : ""}`}
                        onClick={() => setSelectedReportId(String(report.id))}
                      >
                        <div className="kvp-report-list__meta">
                          <strong>{report.public_id}</strong>
                          <span>{humanizeStatus(report.status)}</span>
                        </div>
                        <p>{report.description}</p>
                        <small>{formatDateTime(report.latest_activity_at)}</small>
                      </button>
                    ))}
                  </div>

                  <div className="kvp-report-detail">
                    {selectedReportLoading ? <div className="kvp-report-stream__empty">Rueckmeldung wird geladen...</div> : null}
                    {selectedReportError ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{selectedReportError}</div> : null}
                    {!selectedReportLoading && !selectedReport && !selectedReportError ? (
                      <div className="kvp-report-stream__empty">Waehle links eine Meldung aus.</div>
                    ) : null}

                    {selectedReport ? (
                      <>
                        <div className="kvp-report-detail__head">
                          <div>
                            <strong>{selectedReport.public_id}</strong>
                            <p>{selectedReport.description}</p>
                          </div>
                          <span className="kvp-report-detail__status">{humanizeStatus(selectedReport.status)}</span>
                        </div>
                        <div className="kvp-report-detail__timeline">
                          {(selectedReport.events || []).filter((entry) => entry.visibility_scope === "reporter").length ? (
                            (selectedReport.events || [])
                              .filter((entry) => entry.visibility_scope === "reporter")
                              .map((entry) => (
                                <article key={entry.id} className={`kvp-report-detail__event${entry.event_type === "admin_reply" ? " is-reply" : ""}`}>
                                  <div className="kvp-report-detail__event-meta">
                                    <strong>{entry.event_type === "admin_reply" ? "Antwort aus dem Admin" : "System"}</strong>
                                    <span>{formatDateTime(entry.created_at)}</span>
                                  </div>
                                  <p>{entry.body || "Ohne Zusatztext."}</p>
                                </article>
                              ))
                          ) : (
                            <div className="kvp-report-stream__empty">Zu dieser Meldung gibt es noch keine Rueckmeldung aus der Bearbeitung.</div>
                          )}
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </>
  );
}
